import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Label,
  Modal,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@noor/ui';
import type { ModalRenderProps } from '@noor/lib';
import { Can } from '@/entities/access';
import { useChatOperators } from '@/entities/conversation';
import { useSwitchOperator } from '../model/use-chat-mutations';

/** Hand the chat to another call-center operator (cpanel "Смена оператора"). */
export function SwitchOperatorModal({
  payload,
  close,
}: ModalRenderProps<'switch-operator'>) {
  const { t } = useTranslation('inbox');
  const [operatorId, setOperatorId] = useState('');
  const { data: operators = [], isLoading } = useChatOperators();
  const mutation = useSwitchOperator(payload.chatId);

  const onSubmit = () => {
    if (!operatorId) return;
    mutation.mutate(operatorId, { onSuccess: close });
  };

  return (
    <Can permission="inbox.transfer">
      <Modal
        title={t('switchOperator.title')}
        dismissible={!mutation.isPending}
        footer={
          <>
            <Button
              variant="soft"
              size="modal"
              shape="rounded"
              onClick={close}
              disabled={mutation.isPending}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              variant="dark"
              size="modal"
              shape="rounded"
              onClick={onSubmit}
              disabled={mutation.isPending || !operatorId}
            >
              {t('close.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-1.5">
          <Label htmlFor="switch-operator" className="px-[var(--form-label-padding-x)]">
            {t('switchOperator.select')}
          </Label>
          <Select value={operatorId} onValueChange={setOperatorId} disabled={isLoading}>
            <SelectTrigger id="switch-operator" className="w-full">
              <SelectValue placeholder={t('switchOperator.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {operators.map((operator) => (
                <SelectItem key={String(operator.id)} value={String(operator.id)}>
                  {[operator.first_name, operator.last_name].filter(Boolean).join(' ') ||
                    `#${String(operator.id)}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Modal>
    </Can>
  );
}
